export interface ApiClientOptions {
  readonly baseUrl: string;
  readonly fetch?: typeof globalThis.fetch;
}

export interface SessionResponse {
  readonly subject: string;
  readonly displayName: string | null;
  readonly email: string | null;
}

export interface WorldTopologyManifestResponseV1 {
  readonly universeId: { readonly value: string };
  readonly celestialBodyId: { readonly value: string };
  readonly generatorVersion: string;
  readonly rulesetVersion: { readonly value: string };
  readonly topologyProfile: string;
  readonly strategicCellCount: number;
  readonly regionCount: number;
  readonly contentHash: string;
  readonly activatedAtUtc: string;
}

export interface RegionSummaryResponseV1 {
  readonly regionId: { readonly value: string };
  readonly index: number;
  readonly strategicCellCount: number;
  readonly centroidX: number;
  readonly centroidY: number;
  readonly centroidZ: number;
}

export interface RegionPageResponseV1 {
  readonly regions: ReadonlyArray<RegionSummaryResponseV1>;
  readonly nextCursor: string | null;
}

export class ApiError extends Error {
  public constructor(
    public readonly status: number,
    message: string,
  ) {
    super(message);
    this.name = "ApiError";
  }
}

export const createApiClient = ({
  baseUrl,
  fetch = globalThis.fetch,
}: ApiClientOptions) => {
  const request = async (path: string, init?: RequestInit) => {
    const response = await fetch(new URL(path, baseUrl), init);
    if (!response.ok)
      throw new ApiError(
        response.status,
        `Request failed with status ${response.status}.`,
      );
    return response;
  };

  return {
    health: async (): Promise<boolean> =>
      (await fetch(new URL("/health/ready", baseUrl))).ok,
    session: async (
      accessToken: string,
      signal?: AbortSignal,
    ): Promise<SessionResponse> => {
      const response = await request("/api/v1/session", {
        headers: {
          Authorization: `Bearer ${accessToken}`,
          Accept: "application/json",
        },
        signal: signal ?? null,
      });
      return response.json() as Promise<SessionResponse>;
    },
    worldTopology: async (
      universeId: string,
      bodyId: string,
      signal?: AbortSignal,
    ): Promise<WorldTopologyManifestResponseV1> => {
      const response = await request(
        `/api/v1/universes/${encodeURIComponent(universeId)}/bodies/${encodeURIComponent(bodyId)}/topology`,
        { headers: { Accept: "application/json" }, signal: signal ?? null },
      );
      return response.json() as Promise<WorldTopologyManifestResponseV1>;
    },
    worldRegions: async (
      universeId: string,
      bodyId: string,
      cursor?: string,
      signal?: AbortSignal,
    ): Promise<RegionPageResponseV1> => {
      const query = new URLSearchParams({ limit: "100" });
      if (cursor) query.set("cursor", cursor);
      const response = await request(
        `/api/v1/universes/${encodeURIComponent(universeId)}/bodies/${encodeURIComponent(bodyId)}/regions?${query}`,
        { headers: { Accept: "application/json" }, signal: signal ?? null },
      );
      return response.json() as Promise<RegionPageResponseV1>;
    },
  };
};
